import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { notificationsApi, Notification, NotificationResponse } from '../lib/api/notifications';
import { useAuth } from '@/components/AuthProvider';
import { BusinessAccountContext } from '@/contexts/BusinessAccountContext';
import { useWorkspaceContext } from '@/contexts/WorkspaceContext';
import { useChat } from '@/contexts/ChatContext';
import { useToast } from '@/hooks/use-toast';
import {
  catalogActionButton,
  friendlyNotificationMessage,
  isFailureNotification,
  notificationFixAction,
} from '@/lib/errors';
import { CURRENT_SUBSCRIPTION_KEY } from '@/hooks/useCurrentSubscription';
import { isUnsupportedMessageType } from '@/components/inbox/messages/unsupportedMessage';
import {
  inAppAnnouncementOf,
  isForCurrentSurface,
  isInAppAnnouncementExpired,
  shouldSuppressToast,
} from '@/lib/notifications/inAppNotification';

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  error: Error | null;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
  markConversationAsReadLocal: (conversationId: string) => void;
  refetch: () => void;
}

export const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: React.ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const businessAccountContext = useContext(BusinessAccountContext);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const selectedAccountId = businessAccountContext?.selectedAccountId || null;
  const { selectedWorkspaceId, isLoading: workspacesLoading } = useWorkspaceContext();
  // The list is keyed by workspace, and the workspace reads null until the
  // workspace list lands. Fetching in that gap loaded the 'no-workspace' list,
  // painted it, then re-fetched under the real key — a second spinner on every
  // reload, and briefly the wrong list. With no workspace at all (or an errored
  // workspace list) the wait ends and the 'no-workspace' fetch runs as before.
  const workspacePending = !selectedWorkspaceId && workspacesLoading;
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsList, setNotificationsList] = useState<Notification[]>([]);
  // Which scope's list `notificationsList` currently holds. The list is copied
  // from the query in an effect, so for one render after the data lands the
  // query reads settled while the list is still empty — which painted "No
  // activity yet" for a frame. Tracked per key, not per data object, so the
  // optimistic cache writes (mark read, delete) never read as loading.
  const [appliedKey, setAppliedKey] = useState<string | null>(null);
  const { socket, isConnected } = useChat();
  const currentScope = selectedWorkspaceId || 'no-workspace';
  // The USER is part of the key: a cache keyed only by workspace handed the
  // previous account's bell to whoever signed in next in the same tab.
  const cacheOwner = user?.id || 'anonymous';
  const cacheKey = useMemo<[string, string, string]>(
    () => ['notifications', cacheOwner, currentScope],
    [cacheOwner, currentScope]
  );
  const cacheOwnerRef = useRef(cacheOwner);
  cacheOwnerRef.current = cacheOwner;
  const businessRoomRef = useRef<string | null>(null);
  const [socketAuthenticated, setSocketAuthenticated] = useState(false);
  const latestScopeRef = useRef(currentScope);
  const seenTemplateStatusEventsRef = useRef<Map<string, number>>(new Map());
  const toastedNotificationIdsRef = useRef<Map<string, number>>(new Map());
  useEffect(() => {
    latestScopeRef.current = currentScope;
  }, [currentScope]);

  if (false && !user) {
    const emptyContext: NotificationContextType = {
      notifications: [],
      unreadCount: 0,
      isLoading: false,
      error: null,
      markAsRead: async () => {},
      markAllAsRead: async () => {},
      deleteNotification: async () => {},
      markConversationAsReadLocal: () => {},
      refetch: () => {},
    };

    return (
      <NotificationContext.Provider value={emptyContext}>
        {children}
      </NotificationContext.Provider>
    );
  }

  const {
    data: notificationsData,
    isLoading,
    error,
    refetch,
  } = useQuery<NotificationResponse>({
    queryKey: cacheKey,
    queryFn: async () => {
      const res = await notificationsApi.getAll({
        workspaceId: selectedWorkspaceId || undefined,
        orderBy: 'created_at',
        ascending: false,
        limit: 50,
      });
      if (!Array.isArray(res?.data)) return res;
      return {
        ...res,
        data: res.data.map((n) => ({ ...n, message: friendlyNotificationMessage(n) })),
      };
    },
    enabled: !!user && !workspacePending,
    refetchInterval: false,
    refetchIntervalInBackground: false,
  });

  const cacheKeyString = cacheKey.join('|');
  const listPending = !!notificationsData?.data && appliedKey !== cacheKeyString;

  useEffect(() => {
    if (notificationsData?.data) {
      setNotificationsList(notificationsData.data);
      setUnreadCount(notificationsData.unreadCount || 0);
      setAppliedKey(cacheKeyString);
    } else if (!notificationsData && !isLoading) {
      setNotificationsList([]);
      setUnreadCount(0);
    }
  }, [notificationsData, isLoading, cacheKeyString]);

  const notificationsListRef = useRef<Notification[]>([]);
  useEffect(() => {
    notificationsListRef.current = notificationsList;
  }, [notificationsList]);

  const mutateCache = useCallback(
    (
      scope: string | null | undefined,
      updater: (oldData?: NotificationResponse) => NotificationResponse | undefined
    ) => {
      // 'no-workspace' must match `currentScope` above — this used to write
      // 'no-account', a key no query ever read.
      const key: [string, string, string] = ['notifications', cacheOwnerRef.current, scope || 'no-workspace'];
      queryClient.setQueryData(key, (oldData?: NotificationResponse) => updater(oldData));
    },
    [queryClient]
  );

  const normalizeNotificationPayload = useCallback(
    (payload: any): Notification => {
      const createdAt = payload?.createdAt || payload?.created_at || new Date().toISOString();
      const updatedAt = payload?.updatedAt || payload?.updated_at || createdAt;
      const businessAccountId =
        payload?.businessAccountId ??
        payload?.business_account_id ??
        payload?.businessAccountID ??
        null;
      const data = payload?.data ?? payload?.metadata ?? null;
      const type = payload?.type || payload?.notificationType || 'GENERAL';
      const message = friendlyNotificationMessage({
        type,
        message: payload?.message || payload?.body || '',
        data,
      });

      return {
        id:
          payload?.id ||
          payload?.notificationId ||
          payload?.notification_id ||
          payload?.messageId ||
          `notification-${Date.now()}-${Math.random()}`,
        type,
        title: payload?.title || payload?.message || 'Notification',
        message,
        data,
        isRead: payload?.isRead ?? payload?.is_read ?? false,
        createdAt,
        updatedAt,
        businessAccountId,
        workspaceId: payload?.workspaceId ?? payload?.workspace_id ?? null,
        organizationId: payload?.organizationId ?? payload?.organization_id ?? null,
        userId: payload?.userId ?? payload?.user_id ?? user?.id,
      };
    },
    [user?.id]
  );

  useEffect(() => {
    if (!socket || !isConnected || !user) return;

    const handleNotification = (payload: any) => {
      // Addressed to someone else: a socket that outlived a sign-out or a
      // server-side fan-out bug. Never render another user's notification.
      const addressee = payload?.userId ?? payload?.user_id ?? null;
      if (addressee && addressee !== user?.id) return;

      const normalized = normalizeNotificationPayload(payload);

      // A WhatsApp number was just disconnected or reconnected — refetch the
      // accounts so the connection banner matches now, not on the next poll.
      if (normalized.type === 'ACCOUNT_HEALTH_ALERT') {
        queryClient.invalidateQueries({ queryKey: ['business-accounts-v2'] });
        // The selected account can also come from the workspace query.
        queryClient.invalidateQueries({ queryKey: ['business-accounts-workspace'] });
      }

      const incomingAnnouncement = inAppAnnouncementOf(normalized);
      if (incomingAnnouncement && !isForCurrentSurface(incomingAnnouncement)) {
        return;
      }
      if (incomingAnnouncement && isInAppAnnouncementExpired(incomingAnnouncement)) {
        return;
      }

      /*
       * Which bell this belongs to. A row with a workspace belongs to THAT
       * workspace only. A row with only a business account (live
       * MESSAGE_RECEIVED from an older server, or an unmapped number) belongs
       * to the workspace that owns that account — which is the selected one
       * only if it is the selected account. Only a row with neither is
       * account-level and shows everywhere. This used to fall back to "the
       * workspace that is open" for ANY row without a workspace, so a message
       * for workspace A landed in workspace B's bell.
       */
      const current = latestScopeRef.current;
      const targetScope: string | null = normalized.workspaceId
        ? normalized.workspaceId
        : normalized.businessAccountId
          ? (normalized.businessAccountId === selectedAccountId ? current : null)
          : current;
      if (!targetScope) return;

      // A live MESSAGE_RECEIVED can arrive both as this event and as the
      // client-synthesised row built from `new-message` (see handleIncomingMessage).
      // Replace the synthetic one instead of stacking a duplicate.
      const conversationId = (normalized.data as any)?.conversationId;
      const isSyntheticTwin = (n: Notification) =>
        normalized.type === 'MESSAGE_RECEIVED' &&
        typeof n.id === 'string' &&
        n.id.startsWith('ws-msg-') &&
        !!conversationId &&
        (n.data as any)?.conversationId === conversationId;

      mutateCache(targetScope, (oldData) => {
        const existingNotifications = oldData?.data || [];
        const updatedData = [
          normalized,
          ...existingNotifications.filter(
            (notification) => notification.id !== normalized.id && !isSyntheticTwin(notification)
          ),
        ].slice(0, 50);

        if (!oldData) {
          return {
            success: true,
            data: updatedData,
            unreadCount: normalized.isRead ? 0 : 1,
          };
        }

        return {
          ...oldData,
          data: updatedData,
        };
      });

      if (targetScope === current) {
        const prevList = notificationsListRef.current;
        const alreadyCounted = prevList.some(
          (n) => !n.isRead && (n.id === normalized.id || isSyntheticTwin(n))
        );
        setNotificationsList((prev) =>
          [
            normalized,
            ...prev.filter(
              (notification) => notification.id !== normalized.id && !isSyntheticTwin(notification)
            ),
          ].slice(0, 50)
        );

        if (!normalized.isRead && !alreadyCounted) {
          setUnreadCount((prev) => prev + 1);
        }
      }

      const NOISY_TOAST_SKIP_TYPES = new Set<string>([
        'MESSAGE_RECEIVED',
        'CONVERSATION_UPDATED',
        'NEW_MESSAGE',
        'TYPING',
      ]);
      const shouldToast = (() => {
        if (!normalized.title && !normalized.message) return false;
        if (NOISY_TOAST_SKIP_TYPES.has(String(normalized.type).toUpperCase())) {
          return false;
        }
        if (shouldSuppressToast(normalized.data)) {
          return false;
        }
        if (incomingAnnouncement) {
          return false;
        }
        if (normalized.workspaceId && targetScope !== latestScopeRef.current) {
          return false;
        }
        const now = Date.now();
        const lastSeen = toastedNotificationIdsRef.current.get(normalized.id) || 0;
        if (now - lastSeen < 5000) return false;
        toastedNotificationIdsRef.current.set(normalized.id, now);
        if (toastedNotificationIdsRef.current.size > 250) {
          toastedNotificationIdsRef.current.clear();
        }
        return true;
      })();

      if (shouldToast) {
        const fixButton = catalogActionButton(
          notificationFixAction({
            type: normalized.type,
            title: normalized.title,
            message: payload?.message || payload?.body || '',
            data: normalized.data,
          })
        );
        toast({
          title: normalized.title,
          description: normalized.message || undefined,
          variant: isFailureNotification(normalized.type) ? 'destructive' : 'default',
          ...(fixButton ? { action: fixButton } : {}),
        });
      }

      try {
        window.dispatchEvent(
          new CustomEvent('wenext:notification-received', {
            detail: {
              type: normalized.type,
              data: normalized.data,
              contactId:
                (normalized.data as any)?.contactId ||
                (normalized.data as any)?.contact_id ||
                null,
              reminderId:
                (normalized.data as any)?.reminderId ||
                (normalized.data as any)?.reminder_id ||
                null,
            },
          })
        );
      } catch {
      }
    };

    const handleTemplateStatusUpdate = (payload: any) => {
      const businessAccountId =
        payload?.businessAccountId ?? payload?.business_account_id ?? payload?.businessAccountID ?? null;
      const status = payload?.status;

      if (!businessAccountId || businessAccountId !== selectedAccountId) return;
      const targetScope = businessAccountId;

      if (status !== 'APPROVED') return;

      const templateId = payload?.templateId ?? payload?.template_id ?? '';
      const templateName = payload?.templateName ?? payload?.template_name ?? templateId ?? 'template';
      const dedupeKey = [targetScope, templateId, status].filter(Boolean).join(':');
      const now = Date.now();
      const lastSeen = seenTemplateStatusEventsRef.current.get(dedupeKey) || 0;
      if (now - lastSeen < 1500) return;
      seenTemplateStatusEventsRef.current.set(dedupeKey, now);

      if (seenTemplateStatusEventsRef.current.size > 250) {
        seenTemplateStatusEventsRef.current.clear();
      }

      queryClient.invalidateQueries({ queryKey: ['meta-templates', businessAccountId] });

      if (templateId) {
        queryClient.invalidateQueries({ queryKey: ['meta-template', templateId, businessAccountId] });
      }

      toast({ variant: "success",
        title: 'Template approved',
        description: `Template "${templateName}" is approved and ready to use.`,
      });
    };

    const handleNotificationCountUpdate = (payload: any) => {
      const addressee = payload?.userId ?? payload?.user_id ?? null;
      if (addressee && addressee !== user?.id) return;
      const payloadWorkspaceId =
        payload?.workspaceId ?? payload?.workspace_id ?? null;
      const scope = payloadWorkspaceId || 'no-workspace';
      const unreadValue =
        typeof payload?.unreadCount === 'number'
          ? payload.unreadCount
          : typeof payload?.unread_count === 'number'
          ? payload.unread_count
          : undefined;

      if (typeof unreadValue !== 'number') {
        return;
      }

      mutateCache(payloadWorkspaceId, (oldData) => {
        if (!oldData) {
          return {
            success: true,
            data: [],
            unreadCount: unreadValue,
          };
        }

        return {
          ...oldData,
          unreadCount: unreadValue,
        };
      });

      if (scope === latestScopeRef.current) {
        setUnreadCount(unreadValue);
      }
    };

    const handleEntitlementUpdated = () => {
      queryClient.invalidateQueries({ queryKey: ['organization-entitlements'] });
      queryClient.invalidateQueries({ queryKey: CURRENT_SUBSCRIPTION_KEY });
    };

    const handleIncomingMessage = (data: any) => {
      const isInbound = data.isInbound || data.is_inbound || data.message?.is_inbound || data.message?.isInbound;
      const type = data.type || data.message?.type;

      if (!isInbound && type !== 'new_message') return;
      if (type === 'messages_read' || type === 'status_update') return;

      const msg = data.message || data;
      if (isUnsupportedMessageType(data.messageType || msg.messageType || msg.message_type)) return;
      const conversationId = data.conversationId || msg.conversationId || msg.conversation_id;
      const messageId = data.messageId || msg.messageId || msg.id;
      const contactName = msg.contactName || msg.contact_name || msg.senderPhone || msg.sender_phone || 'Unknown';
      const content = msg.content || msg.content_text || msg.messagePreview || '';
      const businessAccountId = data.businessAccountId || msg.businessAccountId || msg.business_account_id;

      if (!conversationId) return;

      const dedupeId = `ws-msg-${messageId || conversationId}-${Date.now()}`;

      const wsNotification: Notification = {
        id: dedupeId,
        type: 'MESSAGE_RECEIVED',
        title: 'New Message',
        message: `New message from ${contactName}: ${content}`.substring(0, 200),
        data: { conversationId, contactName, messageId, messagePreview: content },
        isRead: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        businessAccountId: businessAccountId || null,
        workspaceId: selectedWorkspaceId || null,
        organizationId: null,
        userId: user?.id,
      };

      /*
       * Only for the account being looked at. A payload with NO business
       * account (Facebook/Instagram) is accepted only when it names this
       * user's own organisation — "no account id" used to mean "show it",
       * which rendered any stray broadcast in every tenant's bell.
       */
      const payloadOrgId = data.organizationId || msg.organizationId || msg.organization_id || null;
      const belongsToCurrentWorkspace = businessAccountId
        ? businessAccountId === selectedAccountId
        : !!payloadOrgId && payloadOrgId === (user as any)?.organization_id;
      if (belongsToCurrentWorkspace) {
        // Decided OUTSIDE the state updater: the badge used to be bumped
        // unconditionally, so every duplicate still added one to the count.
        const isDuplicate = notificationsListRef.current.some(n =>
          n.data?.conversationId === conversationId &&
          n.type === 'MESSAGE_RECEIVED' &&
          ((messageId && n.data?.messageId === messageId) ||
            Math.abs(new Date(n.createdAt).getTime() - Date.now()) < 10000)
        );
        if (isDuplicate) return;
        // The ref is advanced now so a second event in the same tick (the
        // `new-message` + `conversation_updated` pair) sees this row.
        notificationsListRef.current = [wsNotification, ...notificationsListRef.current];
        setNotificationsList((prev) => [wsNotification, ...prev].slice(0, 50));
        setUnreadCount((prev) => prev + 1);
      }
    };

    const handleNewMessageForNotification = (data: any) => {
      const msg = data.message || data;
      const isInbound = msg.is_inbound || msg.isInbound;
      if (isInbound) handleIncomingMessage({ ...msg, ...data, isInbound: true });
    };

    const handleConversationUpdatedForNotification = (data: any) => {
      if (data.type === 'new_message' && (data.isInbound || data.is_inbound)) {
        handleIncomingMessage(data);
      }
    };

    let messagingUsageDebounceTimer: ReturnType<typeof setTimeout> | null = null;
    const handleOutboundMessageForUsage = (data: any) => {
      const msg = data.message || data;
      const isInbound = msg.is_inbound || msg.isInbound;
      if (isInbound) return;
      if (messagingUsageDebounceTimer) clearTimeout(messagingUsageDebounceTimer);
      messagingUsageDebounceTimer = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['messaging-usage'] });
      }, 3000);
    };

    socket.on('notification', handleNotification);
    socket.on('notification_count_update', handleNotificationCountUpdate);
    socket.on('template_status_update', handleTemplateStatusUpdate);
    socket.on('entitlement_updated', handleEntitlementUpdated);
    socket.on('new-message', handleNewMessageForNotification);
    socket.on('new-message', handleOutboundMessageForUsage);
    socket.on('conversation_updated', handleConversationUpdatedForNotification);

    return () => {
      if (messagingUsageDebounceTimer) clearTimeout(messagingUsageDebounceTimer);
      socket.off('notification', handleNotification);
      socket.off('notification_count_update', handleNotificationCountUpdate);
      socket.off('template_status_update', handleTemplateStatusUpdate);
      socket.off('entitlement_updated', handleEntitlementUpdated);
      socket.off('new-message', handleNewMessageForNotification);
      socket.off('new-message', handleOutboundMessageForUsage);
      socket.off('conversation_updated', handleConversationUpdatedForNotification);
    };
  }, [socket, isConnected, user, mutateCache, normalizeNotificationPayload, queryClient, toast, selectedAccountId, selectedWorkspaceId]);

  useEffect(() => {
    if (!socket) return;
    const onAuthenticated = () => setSocketAuthenticated(true);
    const onDisconnect = () => setSocketAuthenticated(false);
    socket.on('authenticated', onAuthenticated);
    socket.on('disconnect', onDisconnect);
    return () => {
      socket.off('authenticated', onAuthenticated);
      socket.off('disconnect', onDisconnect);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket || !isConnected || !socketAuthenticated) {
      if (businessRoomRef.current) {
        businessRoomRef.current = null;
      }
      return;
    }

    const nextRoom = selectedAccountId ? `business-account-${selectedAccountId}` : null;

    if (businessRoomRef.current && businessRoomRef.current !== nextRoom) {
      socket.emit('leave_room', { roomId: businessRoomRef.current });
      businessRoomRef.current = null;
    }

    if (nextRoom && businessRoomRef.current !== nextRoom) {
      socket.emit('join_room', { roomId: nextRoom });
      businessRoomRef.current = nextRoom;
    }

    if (!nextRoom && businessRoomRef.current) {
      socket.emit('leave_room', { roomId: businessRoomRef.current });
      businessRoomRef.current = null;
    }

    return () => {
      if (businessRoomRef.current) {
        socket.emit('leave_room', { roomId: businessRoomRef.current });
        businessRoomRef.current = null;
      }
    };
  }, [socket, isConnected, socketAuthenticated, selectedAccountId]);

  const markAsRead = useCallback(async (id: string) => {
    try {
      if (!user) return;

      if (typeof id === 'string' && id.startsWith('ws-msg-')) {
        const wasUnreadPseudo = notificationsListRef.current.some(
          (n) => n.id === id && !n.isRead
        );
        setNotificationsList((prev) =>
          prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
        );
        if (wasUnreadPseudo) {
          setUnreadCount((prev) => Math.max(0, prev - 1));
        }
        return;
      }

      const existing = notificationsListRef.current.find((n) => n.id === id);
      if (existing && existing.isRead) return;
      const wasUnread = Boolean(existing && !existing.isRead);

      setNotificationsList((prev) =>
        prev.map((notification) =>
          notification.id === id ? { ...notification, isRead: true } : notification
        )
      );

      if (wasUnread) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }

      mutateCache(selectedWorkspaceId, (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          data: oldData.data.map((notification) =>
            notification.id === id ? { ...notification, isRead: true } : notification
          ),
          unreadCount: wasUnread
            ? Math.max(0, (oldData.unreadCount || 0) - 1)
            : oldData.unreadCount,
        };
      });

      await notificationsApi.markAsRead(id);
    } catch (error) {
    }
  }, [mutateCache, selectedWorkspaceId, user]);

  const markAllAsRead = useCallback(async () => {
    try {
      if (!user) return;
      await notificationsApi.markAllAsRead({
        workspaceId: selectedWorkspaceId || undefined,
      });

      setNotificationsList((prev) =>
        prev.map((notification) => ({
          ...notification,
          isRead: true,
        }))
      );

      setUnreadCount(0);

      mutateCache(selectedWorkspaceId, (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          data: oldData.data.map((notification) => ({
            ...notification,
            isRead: true,
          })),
          unreadCount: 0,
        };
      });
    } catch (error) {
    }
  }, [mutateCache, selectedWorkspaceId, user]);

  const deleteNotification = useCallback(async (id: string) => {
    try {
      if (!user) return;
      await notificationsApi.delete(id);

      let removedUnread = false;
      setNotificationsList((prev) =>
        prev.filter((notification) => {
          if (notification.id === id && !notification.isRead) {
            removedUnread = true;
          }
          return notification.id !== id;
        })
      );

      if (removedUnread) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }

      mutateCache(selectedWorkspaceId, (oldData) => {
        if (!oldData) return oldData;

        const notificationToDelete = oldData.data.find((notification) => notification.id === id);
        const wasUnread = notificationToDelete ? !notificationToDelete.isRead : false;

        return {
          ...oldData,
          data: oldData.data.filter((notification) => notification.id !== id),
          unreadCount: wasUnread
            ? Math.max(0, (oldData.unreadCount || 0) - 1)
            : oldData.unreadCount,
        };
      });
    } catch (error) {
    }
  }, [mutateCache, selectedWorkspaceId, user]);

  const markConversationAsReadLocal = useCallback((conversationId: string) => {
    if (!conversationId) return;

    const matching = notificationsListRef.current.filter(
      (n) =>
        !n.isRead &&
        n.type === 'MESSAGE_RECEIVED' &&
        (n.data as any)?.conversationId === conversationId
    );
    if (matching.length === 0) return;

    const matchingSet = new Set(matching.map((n) => n.id));

    setNotificationsList((prev) =>
      prev.map((n) => (matchingSet.has(n.id) ? { ...n, isRead: true } : n))
    );
    setUnreadCount((prev) => Math.max(0, prev - matching.length));

    mutateCache(selectedWorkspaceId, (oldData) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        data: oldData.data.map((n) =>
          matchingSet.has(n.id) ? { ...n, isRead: true } : n
        ),
        unreadCount: Math.max(0, (oldData.unreadCount || 0) - matching.length),
      };
    });
  }, [mutateCache, selectedWorkspaceId]);

  const value = useMemo<NotificationContextType>(() => ({
    notifications: notificationsList,
    unreadCount,
    isLoading: isLoading || workspacePending || listPending,
    error: error as Error | null,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    markConversationAsReadLocal,
    refetch,
  }), [notificationsList, unreadCount, isLoading, workspacePending, listPending, error, markAsRead, markAllAsRead, deleteNotification, markConversationAsReadLocal, refetch]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
